import Icon1 from "@assets/icons/Icon";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface BaseSlidingModalProps {
  children: React.ReactNode;
  onClose: () => void;
  visible?: boolean;
  title?: React.ReactNode;
  startIcon?: React.ReactNode;
  display?: "fullscreen" | "centered";
  width?: string;
}

export default function BaseModal({
  children,
  onClose,
  visible = true,
  title = "Super modal",
  startIcon = <Icon1 name="broadcast" size={20} />,
  display = "centered",
  width = "max-w-2xl",
}: BaseSlidingModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [visible]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && visible) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [visible, onClose]);

  if (!visible) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[10000] bg-shadow_10">
      {isMobile || display === "fullscreen" ? (
        // Mobile: Full screen with scroll
        <div className="h-full w-full bg-white flex flex-col">
          {/* Fixed header */}
          <div className="flex flex-row justify-between p-3 items-center border-b-1 bg-white sticky top-0 z-10">
            <div className="flex flex-row gap-4 pl-2">
              {startIcon}
              <div className="text-h2 font-medium flex items-center">
                {title}
              </div>
            </div>
            <SecondaryButton label="Fermer" onClick={onClose} variant="small" />
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">{children}</div>
        </div>
      ) : (
        // Desktop: Centered modal with max dimensions
        <div className="flex items-center justify-center min-h-full p-4">
          <div
            ref={modalRef}
            className={`bg-white border-1 border-neutral-300 rounded-2xl w-full ${width} max-h-[90vh] flex flex-col`}
          >
            {/* Fixed header */}
            <div className="flex flex-row justify-between p-3 items-center border-b-1">
              <div className="flex flex-row gap-4 pl-2">
                {startIcon}
                <div className="text-h2 font-medium flex items-center">
                  {title}
                </div>
              </div>
              <SecondaryButton
                label="Fermer"
                onClick={onClose}
                variant="small"
              />
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto overflow-x-hidden">{children}</div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}
